// Notification bell for the sidebar header: a bell with an unread badge that
// opens a popover of recent notifications (currently @mentions). Polls the
// unread count, marks items read on open/click, and deep-links to the
// conversation. Self-contained — owns its own fetch + polling.
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Bell, Check } from "lucide-react";
import { api } from "@/lib/api";
import { useAsync } from "@/lib/useAsync";
import { relativeTime } from "@/lib/format";
import type { Notification } from "@/lib/types";

const POLL_MS = 60_000;

export function NotificationBell() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { data, reload } = useAsync(() => api.notifications.list(), []);

  // Poll so the badge stays roughly current without a realtime channel.
  useEffect(() => {
    const iv = window.setInterval(() => reload(), POLL_MS);
    return () => window.clearInterval(iv);
  }, [reload]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const items = data?.notifications ?? [];
  const unread = data?.unreadCount ?? 0;

  // Title line per notification type. Mention interpolates the actor; the rest
  // are system events and ignore it.
  function title(n: Notification): string {
    switch (n.type) {
      case "sla_first_response_breach":
        return t("notifications.slaFirstResponse");
      case "sla_resolution_breach":
        return t("notifications.slaResolution");
      default:
        return t("notifications.mentioned", { actor: n.actorName || t("notifications.someone") });
    }
  }

  async function openItem(n: Notification) {
    setOpen(false);
    // Marking read is best-effort — never let it block navigation or throw.
    if (!n.readAt) {
      try {
        await api.notifications.markRead(n.id);
        reload();
      } catch {
        /* ignore — the badge self-corrects on the next poll */
      }
    }
    if (n.conversationId) navigate(`/conversations/${n.conversationId}`);
  }

  async function markAll() {
    try {
      await api.notifications.markRead();
      reload();
    } catch {
      /* ignore — self-corrects on next poll */
    }
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={t("notifications.title")}
        className="relative flex h-9 w-9 items-center justify-center rounded-md text-muted transition-colors hover:bg-hairline hover:text-ink focus-visible:shadow-ring focus-visible:outline-none"
      >
        <Bell size={18} strokeWidth={2} />
        {unread > 0 ? (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-danger px-1 text-[10px] font-bold leading-none text-white">
            {unread > 9 ? "9+" : unread}
          </span>
        ) : null}
      </button>

      {open ? (
        <div className="absolute right-0 top-full z-50 mt-1 w-80 overflow-hidden rounded-lg border border-hairline bg-panel shadow-lg">
          <div className="flex items-center justify-between border-b border-hairline px-3 py-2">
            <span className="text-label font-semibold text-ink">{t("notifications.title")}</span>
            {items.length > 0 && unread > 0 ? (
              <button
                type="button"
                onClick={() => void markAll()}
                className="flex items-center gap-1 text-meta text-muted hover:text-ink"
              >
                <Check size={13} strokeWidth={2} />
                {t("notifications.markAllRead")}
              </button>
            ) : null}
          </div>

          {items.length === 0 ? (
            <p className="px-3 py-6 text-center text-meta text-muted">{t("notifications.empty")}</p>
          ) : (
            <ul className="max-h-96 overflow-y-auto">
              {items.map((n) => (
                <li key={n.id}>
                  <button
                    type="button"
                    onClick={() => void openItem(n)}
                    className={`flex w-full flex-col gap-0.5 border-b border-hairline px-3 py-2.5 text-left last:border-0 transition-colors hover:bg-hairline ${
                      n.readAt ? "" : "bg-accent-soft"
                    }`}
                  >
                    <span className="text-meta text-ink">{title(n)}</span>
                    {n.preview ? (
                      <span className="line-clamp-2 text-meta text-muted">{n.preview}</span>
                    ) : null}
                    <span className="text-meta text-faint">{relativeTime(n.createdAt, i18n.language)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}
